import {
  clearAllPendingAnswerRecoveries,
  clearPendingAnswerRecoveriesForOtherUsers,
  clearPendingAnswerRecoveriesForUser,
} from '../utils/interviewAnswerRecovery';

const AUTH_TOKEN_KEY = 'authToken';
const AUTH_USER_KEY = 'authUser';
const LEGACY_USER_KEY = 'user';
const LEGACY_INTERVIEW_STORAGE_KEY = 'interviewData';
const INTERVIEW_STORAGE_PREFIX = 'interviewData:';

export type InterviewStorageScope = {
  userId?: string | null;
  jobId?: number | null;
  outsourceId?: number | null;
};

const readStorageValue = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeStorageValue = (key: string, value: string) => {
  try {
    localStorage.setItem(key, value);
  } catch {}
};

const removeStorageValue = (key: string) => {
  try {
    localStorage.removeItem(key);
  } catch {}
};

const safeJsonParse = <T = any>(value: string | null): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
};

const normalizeIdentifier = (value: unknown): string | null => {
  const normalized = String(value ?? '').trim();
  return normalized ? normalized : null;
};

const encodeIdentifier = (value: string | null): string | null => {
  return value ? encodeURIComponent(value) : null;
};

const getLocalStorageKeys = () => {
  try {
    return Array.from({ length: localStorage.length }, (_, index) => localStorage.key(index)).filter(
      (key): key is string => Boolean(key)
    );
  } catch {
    return [];
  }
};

export const readAuthUser = <T = any>(): T | null => {
  return safeJsonParse<T>(readStorageValue(AUTH_USER_KEY));
};

export const resolveAuthUserId = (user?: any): string | null => {
  if (!user || typeof user !== 'object') return null;
  const identifier = user.user_id ?? user.id ?? (typeof user.email === 'string' ? user.email.toLowerCase() : null);
  return normalizeIdentifier(identifier);
};

export const readAuthUserId = (): string | null => {
  return resolveAuthUserId(readAuthUser());
};

export const buildInterviewStorageKey = (scope?: InterviewStorageScope): string | null => {
  const userId = encodeIdentifier(normalizeIdentifier(scope?.userId));
  const jobId = Number(scope?.jobId);
  const outsourceId = Number(scope?.outsourceId);

  if (!userId) return null;
  if (!Number.isFinite(jobId) || jobId <= 0) return null;
  if (!Number.isFinite(outsourceId) || outsourceId <= 0) return null;

  return `${INTERVIEW_STORAGE_PREFIX}${userId}:${jobId}:${outsourceId}`;
};

export const readInterviewCache = <T = any>(scope?: InterviewStorageScope): T | null => {
  const storageKey = buildInterviewStorageKey(scope);
  if (!storageKey) return null;
  return safeJsonParse<T>(readStorageValue(storageKey));
};

export const readLatestInterviewCacheForJob = <T = any>(jobId: number | null | undefined): T | null => {
  const userId = encodeIdentifier(normalizeIdentifier(readAuthUserId()));
  const normalizedJobId = Number(jobId);
  if (!userId || !Number.isFinite(normalizedJobId) || normalizedJobId <= 0) return null;

  const keyPrefix = `${INTERVIEW_STORAGE_PREFIX}${userId}:${normalizedJobId}:`;
  const candidates = getLocalStorageKeys()
    .filter((key) => key.startsWith(keyPrefix))
    .map((key) => safeJsonParse<any>(readStorageValue(key)))
    .filter((value) => (
      value
      && Number(value.interviewId) > 0
      && value.status !== 'technical_interrupted'
    ));

  if (!candidates.length) return null;

  candidates.sort((left, right) => {
    const leftCompleted = left.status === 'completed' ? 1 : 0;
    const rightCompleted = right.status === 'completed' ? 1 : 0;
    if (leftCompleted !== rightCompleted) return rightCompleted - leftCompleted;
    return Number(right.interviewId || 0) - Number(left.interviewId || 0);
  });

  return candidates[0] as T;
};

export const writeInterviewCache = (scope: InterviewStorageScope, value: unknown) => {
  const storageKey = buildInterviewStorageKey(scope);
  if (!storageKey) return;
  writeStorageValue(storageKey, JSON.stringify(value));
};

export const clearInterviewCache = (scope?: InterviewStorageScope) => {
  const storageKey = buildInterviewStorageKey(scope);
  if (!storageKey) return;
  removeStorageValue(storageKey);
};

export const clearLegacyInterviewStorage = () => {
  removeStorageValue(LEGACY_INTERVIEW_STORAGE_KEY);
};

export const clearAllInterviewCaches = () => {
  clearLegacyInterviewStorage();
  clearAllPendingAnswerRecoveries();
  getLocalStorageKeys()
    .filter((key) => key.startsWith(INTERVIEW_STORAGE_PREFIX))
    .forEach((key) => removeStorageValue(key));
};

export const clearInterviewCachesForOtherUsers = (nextUserId: string | null) => {
  clearLegacyInterviewStorage();
  clearPendingAnswerRecoveriesForOtherUsers(nextUserId);
  const encodedUserId = encodeIdentifier(normalizeIdentifier(nextUserId));

  if (!encodedUserId) {
    clearAllInterviewCaches();
    return;
  }

  const userPrefix = `${INTERVIEW_STORAGE_PREFIX}${encodedUserId}:`;
  getLocalStorageKeys()
    .filter((key) => key.startsWith(INTERVIEW_STORAGE_PREFIX) && !key.startsWith(userPrefix))
    .forEach((key) => removeStorageValue(key));
};

export const clearInterviewCachesForUser = (userId: string | null) => {
  clearLegacyInterviewStorage();
  clearPendingAnswerRecoveriesForUser(userId);
  const encodedUserId = encodeIdentifier(normalizeIdentifier(userId));
  if (!encodedUserId) return;

  const userPrefix = `${INTERVIEW_STORAGE_PREFIX}${encodedUserId}:`;
  getLocalStorageKeys()
    .filter((key) => key.startsWith(userPrefix))
    .forEach((key) => removeStorageValue(key));
};

export const clearAuthStorage = () => {
  removeStorageValue(AUTH_TOKEN_KEY);
  removeStorageValue(AUTH_USER_KEY);
  removeStorageValue(LEGACY_USER_KEY);
};

export const replaceAuthSession = (token: string, user: unknown) => {
  const userId = resolveAuthUserId(user);
  clearInterviewCachesForOtherUsers(userId);
  removeStorageValue(LEGACY_USER_KEY);
  writeStorageValue(AUTH_TOKEN_KEY, token);
  writeStorageValue(AUTH_USER_KEY, JSON.stringify(user));
};

export const clearAuthSession = () => {
  clearAuthStorage();
  clearAllInterviewCaches();
};

export const dispatchStorageSync = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('storage'));
};
